// Link: the one link. Blue with a dotted underline that goes solid on
// hover. With a `preview`, it opens that card on hover and focus, with a
// bridge so the pointer can move onto the card; without one, it just goes
// where it points. The look lives in Link.css as `.link`, so plain-HTML
// consumers use the class. Guidelines: Storybook, Components/Link.

import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { ReferencePreview, useReferencePreview, type PreviewPlacement } from '../ReferencePreview';
import './Link.css';

interface Shared {
  /** A ReferencePreviewCard to open on hover and focus. */
  preview?: ReactNode;
  previewPlacement?: PreviewPlacement;
}
type AsLink = Shared & AnchorHTMLAttributes<HTMLAnchorElement> & { as?: 'a' };
type AsButton = Shared & ButtonHTMLAttributes<HTMLButtonElement> & { as: 'button' };
export type LinkProps = AsLink | AsButton;

/** A link (`href`) by default; `as="button"` when it acts in place. */
export const Link = forwardRef<HTMLElement, LinkProps>((props, forwarded) => {
  const { preview, previewPlacement = 'above', className, ...rest } = props;
  const inner = useRef<HTMLElement>(null);
  useImperativeHandle(forwarded, () => inner.current as HTMLElement);
  const id = useId();
  const { previewState, showPreview, scheduleHide, cancelHide, hidePreview } = useReferencePreview();
  const cls = ['link', className].filter(Boolean).join(' ');
  const hover = preview
    ? {
        onMouseEnter: () => showPreview(inner, id, 'link', null),
        onMouseLeave: scheduleHide,
        onFocus: () => showPreview(inner, id, 'link', null),
        onBlur: hidePreview,
      }
    : {};

  const el =
    rest.as === 'button' ? (
      (() => {
        const { as: _as, ...b } = rest as AsButton;
        void _as;
        return <button ref={inner as React.Ref<HTMLButtonElement>} type="button" className={cls} {...hover} {...b} />;
      })()
    ) : (
      (() => {
        const { as: _as, ...a } = rest as AsLink;
        void _as;
        return <a ref={inner as React.Ref<HTMLAnchorElement>} className={cls} {...hover} {...a} />;
      })()
    );

  if (!preview) return el;
  return (
    <>
      {el}
      <ReferencePreview
        targetRef={previewState.targetRef}
        placement={previewPlacement}
        visible={previewState.visible && previewState.id === id}
        onMouseEnter={cancelHide}
        onMouseLeave={scheduleHide}
      >
        {preview}
      </ReferencePreview>
    </>
  );
});

Link.displayName = 'Link';
